import { Link } from "react-router-dom";
import { Mail, MessageCircle, MapPin, Facebook } from "lucide-react";
import { BRAND, WHATSAPP_LINK, CITIES, SOCIAL } from "@/lib/content";
import Logo from "@/components/layout/Logo";

// Custom TikTok SVG icon (lucide doesn't ship one)
function TikTokIcon({ className = "w-4 h-4" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.21a8.27 8.27 0 0 0 4.83 1.54V6.3a4.85 4.85 0 0 1-1.9-.61Z" />
    </svg>
  );
}

export default function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="bg-[#0D1B2A] text-[#E6E1D4] mt-0"
    >
      <div className="container-sp py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="mb-5 [&_*]:!text-white">
              <Logo variant="light" />
            </div>
            <p className="text-[#C8C3B3] leading-relaxed max-w-md">
              Expert guidance for international students coming to Poland —
              admissions, visa and relocation support from people who truly
              know the system.
            </p>
            <div className="mt-6 space-y-2 text-sm text-[#C8C3B3]">
              <a
                href={`mailto:${BRAND.email}`}
                className="flex items-center gap-2 hover:text-white transition-colors"
                data-testid="footer-email"
              >
                <Mail className="w-4 h-4" /> {BRAND.email}
              </a>
              <a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-2 hover:text-white transition-colors"
                data-testid="footer-whatsapp"
              >
                <MessageCircle className="w-4 h-4" /> {BRAND.whatsappDisplay}
              </a>
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 mt-1 shrink-0" />
                <span className="leading-relaxed">{CITIES.join(" · ")}</span>
              </div>
            </div>

            <div className="mt-6 flex gap-2">
              <a
                href={SOCIAL.facebook}
                target="_blank"
                rel="noreferrer"
                aria-label="Facebook"
                data-testid="footer-facebook"
                className="w-10 h-10 rounded-full bg-white/5 hover:bg-[#D7141A] border border-white/10 hover:border-[#D7141A] flex items-center justify-center text-[#C8C3B3] hover:text-white transition-all"
              >
                <Facebook className="w-4 h-4" />
              </a>
              <a
                href={SOCIAL.tiktok}
                target="_blank"
                rel="noreferrer"
                aria-label="TikTok"
                data-testid="footer-tiktok"
                className="w-10 h-10 rounded-full bg-white/5 hover:bg-[#D7141A] border border-white/10 hover:border-[#D7141A] flex items-center justify-center text-[#C8C3B3] hover:text-white transition-all"
              >
                <TikTokIcon />
              </a>
            </div>
          </div>

          <div className="md:col-span-3">
            <div className="overline mb-4 text-[#D7141A]">Pathways</div>
            <ul className="space-y-2 text-[#C8C3B3] text-sm">
              <li><Link to="/bachelor" className="hover:text-white">Bachelor</Link></li>
              <li><Link to="/master" className="hover:text-white">Master</Link></li>
              <li><Link to="/medicine-dentistry" className="hover:text-white">Medicine & Dentistry</Link></li>
              <li><Link to="/phd" className="hover:text-white">PhD</Link></li>
              <li><Link to="/english-exam" className="hover:text-white">English Exam</Link></li>
              <li><Link to="/entry-exams" className="hover:text-white">Entry Exams</Link></li>
              <li><Link to="/visa-relocation" className="hover:text-white">Visa & Relocation</Link></li>
              <li><Link to="/insights" className="hover:text-white">Insights</Link></li>
              <li><Link to="/free-resources" className="hover:text-white">Free Resources</Link></li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <div className="overline mb-4 text-[#D7141A]">Expert</div>
            <p className="text-sm text-[#C8C3B3] leading-relaxed">
              {BRAND.expertName}
              <br />
              <span className="text-[#D7141A]">{BRAND.expertTitle}</span>
            </p>
            <div className="mt-6 flex gap-3 flex-wrap">
              <a
                href="https://studyinpoland.setmore.com"
                target="_blank"
                rel="noreferrer"
                className="btn-gold"
                data-testid="footer-cta-consultation"
              >
                Book Consultation
              </a>
              <Link
                to="/free-resources"
                className="btn-outline"
                style={{ borderColor: "#D7141A", color: "#D7141A" }}
                data-testid="footer-cta-resources"
              >
                Free Guide
              </Link>
            </div>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-[#8A8677]">
          <p>© {new Date().getFullYear()} {BRAND.name}. Expert guidance for studying in Poland.</p>
          <p>Poland-only expertise · Admissions · Visa · Relocation</p>
        </div>
      </div>
    </footer>
  );
}
